import React from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Close } from "@mui/icons-material";
import ImageError from "components/ImageError";
import IconsAccounts from "components/IconsAccounts";
import _request from "actions/_request";
import { threadsTextLength } from "utils/threadsText";
import { THREADS_LIMITS, newThreadsPart } from "../constants";

const ACCENT = "#42C0C7";

const isEmptyPart = (part) => !(part.text || "").trim() && (part.files || []).length === 0;

// Parts 2..N of a Threads chain (OP#3469). Part 1 is the composer's own text and media above; the parts here go
// out as replies to it, in this order, on Threads accounts only.
//
// Props:
// - parts: [{ key, text, files }] — the replies, in order
// - issuesByPart: { [index]: string[] } — messages for a reply, already translated
// - onChange(nextParts)
// - onPickMedia(index) — opens the media dialog for that reply
// - onRemoveMedia(index, fileId)
// - showOtherNetworksNote — non-Threads accounts are selected too
// - reconnectRequired — a selected Threads account must reconnect before it can publish replies
// - tr(key, options)
export default function ThreadsThreadEditor({
  parts,
  issuesByPart,
  onChange,
  onPickMedia,
  onRemoveMedia,
  showOtherNetworksNote,
  reconnectRequired,
  tr,
}) {
  const list = Array.isArray(parts) ? parts : [];
  const total = 1 + list.length;
  const atMax = total >= THREADS_LIMITS.MAX_PARTS;
  const [confirmIndex, setConfirmIndex] = React.useState(null);
  const [focusKey, setFocusKey] = React.useState(null);

  const addPart = () => {
    if (atMax) return;
    const part = newThreadsPart();
    setFocusKey(part.key);
    onChange([...list, part]);
  };

  const updateText = (index, text) => {
    onChange(list.map((p, i) => (i === index ? { ...p, text } : p)));
  };

  const move = (index, delta) => {
    const to = index + delta;
    if (to < 0 || to >= list.length) return;
    const next = [...list];
    const [moved] = next.splice(index, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };

  const removeNow = (index) => {
    onChange(list.filter((_, i) => i !== index));
  };

  const requestRemove = (index) => {
    if (isEmptyPart(list[index])) {
      removeNow(index);
    } else {
      setConfirmIndex(index);
    }
  };

  const addPartButton = (
    <Tooltip
      title={atMax ? tr("postPage.threadsThread.maxParts", { max: THREADS_LIMITS.MAX_PARTS }) : ""}
      disableHoverListener={!atMax}
      disableFocusListener={!atMax}>
      {/* A disabled button fires no events, so the tooltip hangs on this wrapper. */}
      <span>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={addPart}
          disabled={atMax}
          aria-label={list.length === 0 ? tr("postPage.threadsThread.addFirst") : tr("postPage.threadsThread.addPart")}
          sx={{ textTransform: "none", color: ACCENT, fontWeight: 600 }}>
          {list.length === 0 ? tr("postPage.threadsThread.addFirst") : tr("postPage.threadsThread.addPart")}
        </Button>
      </span>
    </Tooltip>
  );

  return (
    <Box className="post-card post-card-sm" data-testid="threads-thread-editor">
      <Box className="post-card-header post-card-header-sm" sx={{ justifyContent: "flex-start", gap: "8px" }}>
        <IconsAccounts socialType="threads" width={18} height={18} />
        <Typography className="post-card-title">{tr("postPage.threadsThread.title")}</Typography>
      </Box>
      <Box sx={{ padding: "12px 20px 16px", display: "flex", flexDirection: "column", gap: "12px" }}>
        <Typography sx={{ fontSize: "12px", color: "var(--text-color)", opacity: 0.7 }}>
          {tr("postPage.threadsThread.hint")}
        </Typography>

        {showOtherNetworksNote && list.length > 0 && (
          <Box sx={{ display: "flex", gap: "6px", alignItems: "flex-start", fontSize: "12px", color: "var(--text-color)" }}>
            <InfoOutlinedIcon sx={{ fontSize: 16, color: ACCENT, mt: "1px" }} />
            <span>{tr("postPage.threadsThread.otherNetworksNote")}</span>
          </Box>
        )}

        {reconnectRequired && list.length > 0 && (
          <Box
            role="alert"
            sx={{
              display: "flex",
              gap: "8px",
              alignItems: "flex-start",
              padding: "8px 10px",
              borderRadius: "8px",
              border: "1px solid rgba(245,166,35,0.45)",
              backgroundColor: "rgba(245,166,35,0.08)",
              color: "var(--text-color)",
              fontSize: "12px",
            }}>
            <WarningAmberIcon sx={{ fontSize: 16, color: "#f5a623", mt: "1px" }} />
            <span>
              {tr("postPage.threadsThread.reconnectHint")}{" "}
              <Link to="/app/accounts" style={{ color: ACCENT, fontWeight: 600 }}>
                {tr("postPage.threadsThread.reconnect")}
              </Link>
            </span>
          </Box>
        )}

        {list.map((part, index) => {
          const len = threadsTextLength(part.text || "");
          const tooLong = len > THREADS_LIMITS.MAX_TEXT_LENGTH;
          const files = Array.isArray(part.files) ? part.files : [];
          const label = tr("postPage.threadsThread.partLabel", { n: index + 2, total });
          const messages = (issuesByPart && issuesByPart[index]) || [];
          return (
            <Box
              key={part.key || index}
              data-testid="threads-part"
              sx={{
                border: "1px solid var(--border-color)",
                borderRadius: "10px",
                padding: "10px 12px",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "var(--text-color)", flex: 1 }}>
                  {label}
                </Typography>
                <Tooltip title={tr("postPage.threadsThread.moveUp")}>
                  <span>
                    <IconButton
                      size="small"
                      aria-label={`${tr("postPage.threadsThread.moveUp")} (${label})`}
                      disabled={index === 0}
                      onClick={() => move(index, -1)}
                      sx={{ color: "var(--text-color)" }}>
                      <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={tr("postPage.threadsThread.moveDown")}>
                  <span>
                    <IconButton
                      size="small"
                      aria-label={`${tr("postPage.threadsThread.moveDown")} (${label})`}
                      disabled={index === list.length - 1}
                      onClick={() => move(index, 1)}
                      sx={{ color: "var(--text-color)" }}>
                      <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={tr("postPage.threadsThread.remove")}>
                  <IconButton
                    size="small"
                    aria-label={`${tr("postPage.threadsThread.remove")} (${label})`}
                    onClick={() => requestRemove(index)}
                    sx={{ color: "var(--text-color)" }}>
                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>

              <Box sx={{ position: "relative" }}>
                <TextField
                  value={part.text || ""}
                  onChange={(e) => updateText(index, e.target.value)}
                  multiline
                  minRows={3}
                  fullWidth
                  autoFocus={focusKey === part.key}
                  placeholder={tr("postPage.threadsThread.textPlaceholder")}
                  inputProps={{ "aria-label": label }}
                  sx={{
                    "& .MuiInputBase-root": { color: "var(--input-text)", fontSize: "14px", paddingBottom: "22px" },
                    "& fieldset": { borderColor: "var(--border-color)" },
                  }}
                />
                <Typography
                  data-testid="threads-part-counter"
                  sx={{
                    position: "absolute",
                    bottom: 6,
                    right: 12,
                    fontSize: "11px",
                    opacity: tooLong ? 1 : 0.55,
                    color: tooLong ? "error.main" : "var(--text-color)",
                  }}>
                  {len} / {THREADS_LIMITS.MAX_TEXT_LENGTH}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "center" }}>
                {files.map((id) => (
                  <Box
                    key={id}
                    className="post-media-thumb"
                    sx={{ width: "64px !important", height: "64px !important", "&:hover .threads-media-remove, &:focus-within .threads-media-remove": { opacity: 1 } }}>
                    <ImageError
                      src={_request.files.formLink(id)}
                      style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "8px" }}
                    />
                    {/* Shown on hover, and kept in the tab order (opacity, not display) so a keyboard can reach it. */}
                    <IconButton
                      className="threads-media-remove"
                      aria-label={tr("postPage.threadsThread.removeMedia")}
                      onClick={() => onRemoveMedia(index, id)}
                      sx={{
                        position: "absolute",
                        top: 2,
                        right: 2,
                        opacity: 0,
                        width: 18,
                        height: 18,
                        padding: 0,
                        backgroundColor: "rgba(0,0,0,0.65)",
                        "&:hover": { backgroundColor: "rgba(0,0,0,0.9)" },
                      }}>
                      <Close sx={{ fontSize: 12, color: "#fff" }} />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  size="small"
                  startIcon={<AddPhotoAlternateOutlinedIcon />}
                  onClick={() => onPickMedia(index)}
                  aria-label={`${tr("postPage.threadsThread.addMedia")} (${label})`}
                  sx={{ textTransform: "none", color: ACCENT }}>
                  {tr("postPage.threadsThread.addMedia")}
                </Button>
              </Box>

              {messages.length > 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                  {messages.map((m, i) => (
                    <Typography key={i} sx={{ fontSize: "12px", color: "#e53935" }}>
                      ✕ {m}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          );
        })}

        <Box>{addPartButton}</Box>
      </Box>

      <Dialog
        open={confirmIndex !== null}
        onClose={() => setConfirmIndex(null)}
        sx={{ "& .MuiPaper-root": { backgroundColor: "var(--background-menu)", color: "var(--text-color)", borderRadius: "10px" } }}>
        <DialogTitle sx={{ color: "var(--text-color)", fontWeight: 700 }}>
          {tr("postPage.threadsThread.remove")}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "13px", color: "var(--text-color)" }}>
            {confirmIndex !== null &&
              tr("postPage.threadsThread.removeConfirm", { n: confirmIndex + 2 })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmIndex(null)} sx={{ color: "var(--text-color)" }}>
            {tr("cancel")}
          </Button>
          <Button
            onClick={() => {
              const index = confirmIndex;
              setConfirmIndex(null);
              if (index !== null) removeNow(index);
            }}
            sx={{ backgroundColor: ACCENT, color: "#fff", "&:hover": { backgroundColor: "#38a8ae" } }}>
            {tr("postPage.threadsThread.remove")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
